import React, { useCallback, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import LinearProgress from '@mui/material/LinearProgress';

import {
    type IPlaybookV2,
    type PlaybookVariable,
    usePlaybookVariables,
} from 'src/services/api';
import { useNotifications } from 'src/notifications';
import { type IPlaybookTemplate } from 'src/pages/landlord/playbooks/new/constants';
import { usePlaybookForm } from 'src/pages/landlord/playbooks/components/editor/use-playbook-form';

type IPlaybookEditorContext = {
    isEditing: boolean;
    playbookVariables: PlaybookVariable[];
    formRef: React.RefObject<HTMLFormElement>;

    isDraggingPlaybookTrigger: boolean;
    setIsDraggingPlaybookTrigger: (isDragging: boolean) => void;
    isDraggingPlaybookStep: boolean;
    setIsDraggingPlaybookStep: (isDragging: boolean) => void;

    isShowPlaybookFilterInfoModal: boolean;
    setShowPlaybookFilterInfoModal: (isShow: boolean) => void;
};

// @ts-expect-error lazy init
const PlaybookEditorContext = React.createContext<IPlaybookEditorContext>();

export const usePlaybookEditorContext = () => {
    const context = React.useContext(PlaybookEditorContext);

    if (!context) {
        throw new Error('usePlaybookEditorContext must be used within a PlaybookEditorProvider');
    }

    return context;
};

type Props = {
    playbook?: IPlaybookV2;
    playbookTemplate?: IPlaybookTemplate;
    children: React.ReactNode;
};

export default function PlaybookEditorProvider({
    playbook,
    playbookTemplate,
    children,
}: Props) {
    const { addNotification } = useNotifications();
    const { data: playbookVariables } = usePlaybookVariables();

    const { form, submit } = usePlaybookForm(playbook, playbookTemplate);
    const formRef = useRef<HTMLFormElement>(null);

    const [isDraggingPlaybookTrigger, _setIsDraggingPlaybookTrigger] = useState(false);
    const [isDraggingPlaybookStep, _setIsDraggingPlaybookStep] = useState(false);
    const [isShowPlaybookFilterInfoModal, setShowPlaybookFilterInfoModal] = useState(false);

    const setIsDraggingPlaybookTrigger = useCallback((isDragging: boolean) => {
        if (isDragging) {
            _setIsDraggingPlaybookTrigger(true);
        } else {
            setTimeout(() => {
                _setIsDraggingPlaybookTrigger(false);
            }, 300);
        }
    }, []);

    const setIsDraggingPlaybookStep = useCallback((isDragging: boolean) => {
        if (isDragging) {
            _setIsDraggingPlaybookStep(true);
        } else {
            setTimeout(() => {
                _setIsDraggingPlaybookStep(false);
            }, 300);
        }
    }, []);

    const isLoading = !playbookVariables;
    if (isLoading) {
        return (
            <LinearProgress />
        );
    }

    const onError = () => {
        addNotification('Invalid input. Please check the form for errors.', 'error');
    };

    return (
        <PlaybookEditorContext.Provider
            value={{
                isEditing: !!playbook,
                playbookVariables,
                formRef,

                isDraggingPlaybookTrigger,
                setIsDraggingPlaybookTrigger,
                isDraggingPlaybookStep,
                setIsDraggingPlaybookStep,

                isShowPlaybookFilterInfoModal,
                setShowPlaybookFilterInfoModal,
            }}
        >
            <DndProvider backend={HTML5Backend}>
                <form ref={formRef} onSubmit={form.handleSubmit(submit, onError)}>
                    <FormProvider {...form}>
                        {children}
                    </FormProvider>
                </form>
            </DndProvider>
        </PlaybookEditorContext.Provider>
    );
}